// UserMenu — Phase-1 nav redesign: the user identity + Sign out, relocated from
// the old sidebar footer into a top-right avatar dropdown. Presentational only —
// the sign-out handler (including the HIPAA queryClient.clear() PHI-cache wipe)
// stays in AppShell and is passed in as `onSignOut`, so the security-critical
// sequence is unchanged and lives in one auditable place.
//
// A plain disclosure (button + popover), NOT an ARIA menu — it holds one action
// and matches the sibling top-bar dropdowns (PracticeSwitcher / NotificationBell),
// which are also plain popovers.

import React, { useState, useRef, useEffect } from 'react';
import { LogOut, ChevronDown, User } from 'lucide-react';

interface UserMenuProps {
  initials: string;
  name: string;
  rank: string;
  onSignOut: () => void;
}

export default function UserMenu({ initials, name, rank, onSignOut }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const hasIdentity = initials !== '';

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      className="relative flex-shrink-0"
      ref={ref}
      onBlur={(e) => {
        if (ref.current && !ref.current.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="User menu"
        className="flex items-center gap-1.5 rounded-full hover:bg-gray-100 transition-colors py-0.5 pl-0.5 pr-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C94B1F] focus-visible:ring-offset-1"
      >
        {hasIdentity ? (
          <span className="w-8 h-8 rounded-full bg-[#C94B1F] text-white flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-[0_0_0_1px_rgba(17,24,39,0.06),inset_0_1px_0_rgba(255,255,255,0.14)]">
            {initials}
          </span>
        ) : (
          <span className="w-8 h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4" aria-hidden="true" />
          </span>
        )}
        <ChevronDown
          className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
          {hasIdentity && (name || rank) && (
            <div className="px-4 py-3 border-b border-gray-100">
              {name && <p className="text-sm font-medium text-gray-900 truncate">{name}</p>}
              {rank && <p className="text-xs text-gray-400 truncate">{rank}</p>}
            </div>
          )}
          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#C94B1F]"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
